import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { ReasonDialog } from "../../components/dash/ReasonDialog";
import { formatRupiah, formatDay, isManagerRole } from "../../lib/labels";
import { Section, Pill, ProofLink, CONTRACT_STATUS, SCHEME_NAMES, pct } from "./shared";
import { ContractForm } from "./ContractForm";
import { AgreementArchives } from "../../components/AgreementArchives";

const Info = ({ label, value, testid }) => (
  <div><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p><p className="mt-1 text-sm font-semibold text-slate-900 break-words" data-testid={testid}>{value ?? "-"}</p></div>
);

const TermsView = ({ t, prefix }) => (
  <div className="space-y-4" data-testid={`${prefix}-terms`}>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Info label="Skema" value={SCHEME_NAMES[t.scheme] || t.scheme} testid={`${prefix}-scheme`} />
      <Info label="Modal Investor" value={formatRupiah(t.capitalAmount)} testid={`${prefix}-capital`} />
      <Info label="Target BEP" value={`Tahun ke-${t.bepTargetYears} (bukan jaminan)`} />
      <Info label="Masa Kerja Sama" value={`${t.durationYears} tahun · ${formatDay(t.startDate)} – ${formatDay(t.endDate)}`} />
      <Info label="Tanggal Perjanjian" value={formatDay(t.agreementDate)} />
      <Info label="Jumlah Batang" value={Number(t.treeCount).toLocaleString("id-ID")} />
      <Info label="Pemilik Lahan" value={t.landownerName || "-"} />
      <Info label="Cadangan Kebun" value={`${pct(t.reserveBp ?? 500)} omzet`} />
      <Info label={t.sjiInSplit ? "Bagian PT SJI" : "Imbalan Jasa PT SJI"} value={t.sjiInSplit ? `${pct(t.imbalanPreBp)} sebelum BEP · ${pct(t.imbalanPostBp)} sesudah BEP · termasuk dalam pembagian 100% laba kebun, tidak dari modal` : t.imbalanPreBp ? `${pct(t.imbalanPreBp)} sebelum BEP · ${pct(t.imbalanPostBp)} sesudah BEP · dari hasil bersih kebun, tidak dari modal` : t.platformFeeBp ? `${pct(t.platformFeeBp)} sesudah BEP (perjanjian lama) · dari hasil bersih kebun` : "Belum ditetapkan"} testid={`${prefix}-platform-fee`} />
    </div>
    <div className="overflow-x-auto"><table className="w-full text-sm min-w-[420px]" data-testid={`${prefix}-shares`}>
      <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="py-2 pr-3">Fase</th><th>Pemodal</th><th>Pengelola</th><th>Pemilik Lahan</th>{t.sjiInSplit && <th>PT SJI</th>}</tr></thead>
      <tbody className="divide-y divide-slate-100 font-mono">
        <tr><td className="py-2 pr-3 font-sans">Sebelum BEP</td>{t.pre.map((b, i) => <td key={i}>{pct(b)}</td>)}{t.sjiInSplit && <td>{pct(t.imbalanPreBp)}</td>}</tr>
        <tr><td className="py-2 pr-3 font-sans">Sesudah BEP</td>{t.post.map((b, i) => <td key={i}>{pct(b)}</td>)}{t.sjiInSplit && <td>{pct(t.imbalanPostBp)}</td>}</tr>
      </tbody>
    </table></div>
    {t.notes && <p className="text-sm text-slate-600 whitespace-pre-line">{t.notes}</p>}
  </div>
);

const call = async (url, body, reload, msg) => {
  try { await api.post(url, body); toast.success(msg); reload(); return true; } catch (e) { toast.error(formatApiError(e)); return false; }
};

const Upload = ({ c, kind, reload }) => (
  <label className="inline-flex items-center justify-center h-9 px-3 rounded-md border border-emerald-800 text-emerald-800 text-sm font-semibold cursor-pointer" data-testid={`upload-${kind}-btn`}>
    {kind === "agreement" ? "Unggah Dokumen Perjanjian (PDF)" : "Tambah Lampiran (JPG/PNG)"}
    <input type="file" hidden accept={kind === "agreement" ? "application/pdf" : "image/jpeg,image/png"} data-testid={`upload-${kind}-input`} onChange={async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      try { await api.post(`/profit/contracts/${c.id}/${kind === "agreement" ? "agreement" : "attachments"}`, fd); toast.success("Dokumen terunggah"); reload(); }
      catch (err) { toast.error(formatApiError(err)); }
      e.target.value = "";
    }} />
  </label>
);

const Actions = ({ c, p, reload, onEdit }) => {
  const { user } = useAuth();
  const [dlg, setDlg] = useState(null);
  const [agree, setAgree] = useState(false);
  const [checked, setChecked] = useState(false);
  const u = `/profit/contracts/${c.id}`;
  const editable = ["DRAFT", "REVISION_REQUIRED"].includes(p.status);
  return (
    <div className="space-y-3">
      {isManagerRole(user.role) && editable && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onEdit} data-testid="edit-contract-btn">Ubah Data</Button>
          <Upload c={c} kind="agreement" reload={reload} /><Upload c={c} kind="attachment" reload={reload} />
          <Button size="sm" disabled={!p.agreementFileId} onClick={() => call(`${u}/submit`, {}, reload, "Dikirim untuk persetujuan investor")} className="bg-emerald-800 hover:bg-emerald-900" data-testid="submit-contract-btn">Kirim untuk Persetujuan Investor</Button>
        </div>
      )}
      {isManagerRole(user.role) && ["DRAFT", "REVISION_REQUIRED", "SUBMITTED_TO_INVESTOR"].includes(p.status) &&
        <Button variant="ghost" size="sm" className="text-red-700" onClick={() => setDlg("cancel")} data-testid="cancel-proposal-btn">{p.isAmendment ? "Batalkan Amendment" : "Batalkan Skema"}</Button>}
      {user.role === "INVESTOR" && p.status === "SUBMITTED_TO_INVESTOR" && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-3" data-testid="investor-approval">
          <Button asChild variant="outline" size="sm" data-testid="approval-view-assumptions-btn"><Link to="/asumsi-bagi-hasil" target="_blank">LIHAT ASUMSI BAGI HASIL</Link></Button>
          <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="approve-agree" />
            Saya telah membaca dokumen perjanjian dan menyetujui data skema di atas. Saya memahami PT SJI tidak menjamin modal, hasil, atau keuntungan.</label>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={!agree} onClick={() => call(`${u}/confirm`, { agree }, reload, "Skema disetujui. Menunggu pemeriksaan PT SJI.")} className="bg-emerald-800 hover:bg-emerald-900" data-testid="approve-contract-btn">Setujui</Button>
            <Button size="sm" variant="outline" onClick={() => setDlg("revise")} data-testid="request-revision-btn">Minta Koreksi</Button>
          </div>
        </div>
      )}
      {user.role === "SUPER_ADMIN" && (
        <div className="space-y-3" data-testid="admin-review">
          {p.status === "INVESTOR_CONFIRMED" && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 space-y-3">
              <p className="text-sm text-slate-700">PT SJI memeriksa kesesuaian data form dengan dokumen perjanjian. PT SJI tidak dapat mengubah persentase; bila tidak sesuai, pilih Minta Revisi.</p>
              <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid="lock-checked" />
                Data form sudah saya cocokkan dengan dokumen perjanjian dan konfirmasi Investor.</label>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" disabled={!checked} onClick={() => call(`${u}/lock`, { checked, note: "" }, reload, "Skema LOCKED")} className="bg-emerald-800 hover:bg-emerald-900" data-testid="lock-scheme-btn"><Lock className="h-4 w-4 mr-1" />LOCK Skema</Button>
                <Button size="sm" variant="outline" onClick={() => setDlg("revise")} data-testid="admin-revision-btn">Minta Revisi</Button>
              </div>
            </div>
          )}
          <Button size="sm" variant="ghost" onClick={() => setDlg("note")} data-testid="admin-note-btn">Tambah Catatan PT SJI</Button>
        </div>
      )}
      <ReasonDialog open={dlg === "revise"} onOpenChange={(o) => !o && setDlg(null)} title={user.role === "INVESTOR" ? "Minta koreksi ke Kelompok Tani/Koperasi" : "Minta revisi skema"} confirmLabel="Kirim"
        onConfirm={(note) => call(`${u}/request-revision`, { note }, reload, "Permintaan dikirim")} />
      <ReasonDialog open={dlg === "note"} onOpenChange={(o) => !o && setDlg(null)} title="Catatan PT SJI" confirmLabel="Simpan"
        onConfirm={(note) => call(`${u}/note`, { note }, reload, "Catatan tersimpan")} />
      <ReasonDialog open={dlg === "cancel"} onOpenChange={(o) => !o && setDlg(null)} title="Batalkan pengajuan" danger confirmLabel="Batalkan"
        onConfirm={(note) => call(`${u}/cancel`, { note }, reload, "Dibatalkan")} />
    </div>
  );
};

const ACTION_LABEL = { CREATE: "Dibuat", AMENDMENT_CREATE: "Amendment dibuat", UPDATE: "Data diubah", AGREEMENT_UPLOAD: "Dokumen perjanjian diunggah",
  ATTACHMENT_UPLOAD: "Lampiran diunggah", SUBMIT: "Dikirim ke investor", INVESTOR_CONFIRM: "Disetujui investor", REVISION_INVESTOR: "Investor minta koreksi",
  REVISION_SUPER_ADMIN: "PT SJI minta revisi", PT_SJI_NOTE: "Catatan PT SJI", CANCEL: "Dibatalkan", LOCK: "LOCKED PT SJI" };

const Proposal = ({ c, p, reload, masters }) => {
  const [editing, setEditing] = useState(false);
  if (editing) return <ContractForm contract={c} terms={p.terms} masters={masters} onDone={() => { setEditing(false); reload(); }} onCancel={() => setEditing(false)} />;
  return (
    <Section title={p.isAmendment ? `Amendment — Version ${p.version}` : `Pengajuan Skema — Version ${p.version}`} testid="proposal-section"
      action={<Pill map={CONTRACT_STATUS} value={p.status} testid="proposal-status" />}>
      {p.status === "REVISION_REQUIRED" && <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="revision-note">Catatan {p.revisionBy === "INVESTOR" ? "Investor" : "PT SJI"}: {p.revisionNote}</p>}
      <TermsView t={p.terms} prefix="proposal" />
      <div className="mt-4 flex flex-wrap gap-3 text-sm">
        <span>Dokumen perjanjian: {p.agreementFileId ? <ProofLink id={p.agreementFileId} label="Lihat PDF" testid="proposal-agreement-link" /> : <b className="text-red-700" data-testid="proposal-no-agreement">belum diunggah</b>}</span>
        {(p.attachmentIds || []).map((id, i) => <ProofLink key={id} id={id} label={`Lampiran ${i + 1}`} testid={`proposal-attachment-${i}`} />)}
      </div>
      <div className="mt-4"><Actions c={c} p={p} reload={reload} onEdit={() => setEditing(true)} /></div>
      <ul className="mt-4 space-y-1 text-xs text-slate-600" data-testid="proposal-history">
        {(p.history || []).map((h, i) => <li key={i}>{formatDay(h.at)} · {ACTION_LABEL[h.action] || h.action} · {h.byName || h.role}{h.note && ` — ${h.note}`}</li>)}
      </ul>
    </Section>
  );
};

const downloadPdf = async (c, v) => {
  try {
    const { data } = await api.get(`/profit/contracts/${c.id}/versions/${v}/pdf`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = `Ringkasan-Skema-${c.projectCode || "kebun"}-v${v}.pdf`;
    a.click();
  } catch (e) { toast.error("Gagal mengunduh PDF ringkasan"); }
};

export const ContractTab = ({ data, reload }) => {
  const { user } = useAuth();
  const c = data.contract;
  const p = c.proposal;
  const locked = c.status === "LOCKED";
  const amend = () => call(`/profit/contracts/${c.id}/amendment`, {}, reload, "Amendment dibuat sebagai draf");
  return (
    <div className="space-y-6">
      {locked && (
        <Section title={`Skema Aktif — Terkunci (Version ${c.version})`} testid="locked-scheme"
          action={isManagerRole(user.role) && (!p || p.status === "CANCELLED") && <Button variant="outline" size="sm" onClick={amend} data-testid="create-amendment-btn">Buat Amendment / Perubahan Perjanjian</Button>}>
          <TermsView t={c} prefix="locked" />
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            <Info label="Dikunci PT SJI" value={formatDay(c.lockedAt)} testid="locked-at" />
            <Info label="Disetujui Investor" value={formatDay(c.investorConfirmation?.at)} />
            <Info label="BEP Aktual" value={c.bepReached ? `Tercapai ${formatDay(c.bepReachedAt)}` : "Belum tercapai"} testid="info-bep" />
          </div>
          <div className="mt-4"><ProofLink id={c.agreementFileId} label="Dokumen perjanjian (PDF)" testid="locked-agreement-link" /></div>
          <p className="mt-3 text-xs text-slate-500">Persentase tidak dapat diubah oleh siapa pun. Perubahan hanya melalui Amendment yang disetujui Investor, didukung dokumen baru, dan di-LOCK PT SJI sebagai versi baru.</p>
        </Section>
      )}
      {p && p.status !== "CANCELLED" && <Proposal c={c} p={p} reload={reload} masters={data.masters} />}
      {!locked && c.status === "CANCELLED" && <p className="text-sm text-slate-600" data-testid="contract-cancelled">Skema dibatalkan. Kelompok Tani/Koperasi dapat membuat skema baru untuk kebun ini.</p>}
      {data.versions?.length > 0 && (
        <Section title="Riwayat Versi Skema" testid="versions-section">
          <div className="space-y-2">
            {data.versions.map((v) => (
              <div key={v.version} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`version-${v.version}`}>
                <span><b>Version {v.version}</b>{v.isAmendment && " (Amendment)"} · LOCKED {formatDay(v.lockedAt)}
                  {Object.keys(v.changes || {}).length > 0 && <span className="block text-xs text-slate-500">Berubah: {Object.keys(v.changes).join(", ")}</span>}</span>
                <span className="flex flex-wrap gap-2"><ProofLink id={v.documentReference} label="Perjanjian" testid={`version-doc-${v.version}`} />
                  <Button size="sm" variant="outline" onClick={() => downloadPdf(c, v.version)} data-testid={`version-pdf-${v.version}`}><Download className="h-4 w-4 mr-1" />Ringkasan PDF</Button></span>
              </div>
            ))}
          </div>
        </Section>
      )}
      <AgreementArchives contractId={c.id} refreshKey={`${c.status}-${c.version}-${p?.status || ""}`} testid="contract-archives" />
    </div>
  );
};
